import { enableProdMode, importProvidersFrom } from '@angular/core';

import {
  HttpClient,
  provideHttpClient,
  withInterceptors,
} from '@angular/common/http';
import { ReactiveFormsModule } from '@angular/forms';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter } from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { CookieService } from 'ngx-cookie-service';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { cacheInterceptor } from './app/core/interceptors/cache.interceptor';
import { rumHttpErrorInterceptor } from './app/core/interceptors/rum-http-error.interceptor';
import { tokenInterceptor } from './app/core/interceptors/token.interceptor';
import { environment } from './environments/environment';
import { datadogRum } from '@datadog/browser-rum';

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

if (environment.production) {
  enableProdMode();
}

datadogRum.init({
  applicationId: environment.appid,
  clientToken: environment.clientToken,
  site: 'datadoghq.eu',
  service: 'angularproductionview',
  env: environment.environmentName,
  sessionSampleRate: 100,
  sessionReplaySampleRate: 0,
  trackResources: true,
  trackUserInteractions: false,
  trackLongTasks: false,
  defaultPrivacyLevel: 'mask',
  beforeSend: (event) => {
    const sanitizeUrl = (value: string) => {
      const url = new URL(value, window.location.origin);
      url.search = '';
      url.hash = '';
      url.username = '';
      url.password = '';
      return url.href;
    };

    event.view.url = sanitizeUrl(event.view.url);
    if (event.view.referrer) {
      event.view.referrer = sanitizeUrl(event.view.referrer);
    }
    if (event.type === 'resource') {
      event.resource.url = sanitizeUrl(event.resource.url);
    }
    if (event.type === 'error' && event.error.resource) {
      event.error.resource.url = sanitizeUrl(event.error.resource.url);
    }

    if (event.type === 'resource') {
      return true;
    }

    return (
      event.type === 'error' &&
      event.error.source === 'custom' &&
      event.error.message.startsWith('[HTTP] ')
    );
  },
});

bootstrapApplication(AppComponent, {
  providers: [
    importProvidersFrom(
      BrowserModule,
      ReactiveFormsModule,
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      }),
    ),
    CookieService,
    provideAnimations(),
    provideHttpClient(
      withInterceptors([
        tokenInterceptor,
        cacheInterceptor,
        rumHttpErrorInterceptor,
      ]),
    ),
    provideRouter(routes),
  ],
}).catch((err) => console.error(err));
